import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_NAV_ITEMS } from '../../config/navigation';
import apiService from '../../services/ApiService';

/**
 * Palette comandi (Ctrl+K / ⌘K): salta a qualunque pagina o direttamente
 * alla scheda di un tool senza passare dalla lista. I tool si caricano alla
 * prima apertura e restano in memoria finché la pagina è aperta.
 */
interface PaletteTool {
    id: number;
    name: string;
    code: string;
    status: string;
}

interface Command {
    id: string;
    group: 'Pagine' | 'Tool' | 'Azioni';
    label: string;
    hint?: string;
    icon: string;
    productIcon?: string;
    search: string;
    run: () => void;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSignOut: () => void;
}

let toolsCache: PaletteTool[] | null = null;

const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const CommandPalette: React.FC<Props> = ({ open, onClose, onSignOut }) => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const [tools, setTools] = useState<PaletteTool[]>(toolsCache ?? []);
    const [toolsState, setToolsState] = useState<'idle' | 'loading' | 'ready' | 'error'>(toolsCache ? 'ready' : 'idle');

    useEffect(() => {
        if (!open) return;
        setQuery('');
        setActive(0);
        window.setTimeout(() => inputRef.current?.focus(), 0);
        if (toolsCache || toolsState === 'loading') return;
        setToolsState('loading');
        apiService.adminGetTools<PaletteTool>()
            .then(data => { toolsCache = data; setTools(data); setToolsState('ready'); })
            .catch(() => setToolsState('error'));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const commands = useMemo<Command[]>(() => {
        const go = (to: string) => () => { navigate(to); onClose(); };
        const pages: Command[] = ADMIN_NAV_ITEMS.map(item => ({
            id: `page:${item.to}`,
            group: 'Pagine',
            label: item.label,
            hint: item.description,
            icon: item.icon,
            productIcon: item.productIcon,
            search: normalize(`${item.label} ${item.description} ${item.keywords ?? ''}`),
            run: go(item.to),
        }));
        const quality: Command[] = [
            { id: 'page:/quality/issues', group: 'Pagine', label: 'Problemi da gestire', hint: 'Quality Center › Problemi', icon: 'bi-exclamation-diamond', search: 'problemi issue triage quality', run: go('/quality/issues') },
            { id: 'page:/quality/reports', group: 'Pagine', label: 'Segnalazioni utenti', hint: 'Quality Center › Segnalazioni', icon: 'bi-flag', search: 'segnalazioni report utenti quality', run: go('/quality/reports') },
            { id: 'page:/users?pending', group: 'Pagine', label: 'Utenti in attesa di approvazione', hint: 'Utenti › filtro In attesa', icon: 'bi-person-check', search: 'utenti attesa approvazione pending approva', run: go('/users?approval=Pending') },
            { id: 'page:/messages/new', group: 'Pagine', label: 'Nuova comunicazione', hint: 'Comunicazioni › Scrivi un avviso', icon: 'bi-pencil-square', search: 'nuova comunicazione avviso messaggio scrivi bacheca', run: go('/messages/new') },
        ];
        const toolCommands: Command[] = tools.map(t => ({
            id: `tool:${t.id}`,
            group: 'Tool',
            label: t.name,
            hint: t.code,
            icon: 'bi-box',
            search: normalize(`${t.name} ${t.code}`),
            run: go(`/tools/${t.id}`),
        }));
        const actions: Command[] = [
            { id: 'action:signout', group: 'Azioni', label: 'Esci dall’area admin', icon: 'bi-box-arrow-right', search: 'esci logout signout', run: () => { onClose(); onSignOut(); } },
        ];
        return [...pages, ...quality, ...toolCommands, ...actions];
    }, [tools, navigate, onClose, onSignOut]);

    const results = useMemo(() => {
        const q = normalize(query.trim());
        if (!q) return commands.filter(c => c.group !== 'Tool').slice(0, 14);
        const terms = q.split(/\s+/);
        return commands.filter(c => terms.every(t => c.search.includes(t) || normalize(c.label).includes(t))).slice(0, 30);
    }, [commands, query]);

    useEffect(() => { setActive(0); }, [query]);

    useEffect(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    if (!open) return null;

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(i + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); results[active]?.run(); }
        else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };

    let lastGroup = '';

    return (
        <div className="cmdk-backdrop" onMouseDown={onClose}>
            <div
                className="cmdk"
                role="dialog"
                aria-modal="true"
                aria-label="Palette comandi"
                onMouseDown={e => e.stopPropagation()}
                onKeyDown={onKeyDown}>
                <div className="cmdk__search">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Cerca una pagina o un tool…"
                        aria-label="Cerca"
                        aria-controls="cmdk-list"
                        aria-activedescendant={results[active] ? `cmdk-${active}` : undefined}
                        role="combobox"
                        aria-expanded="true"
                    />
                    <kbd>Esc</kbd>
                </div>
                <div className="cmdk__list" id="cmdk-list" role="listbox" ref={listRef}>
                    {results.length === 0 && (
                        <div className="cmdk__empty">
                            Nessun risultato per “{query}”.
                            {toolsState === 'loading' && ' Sto ancora caricando i tool…'}
                        </div>
                    )}
                    {results.map((cmd, index) => {
                        const header = cmd.group !== lastGroup ? cmd.group : null;
                        lastGroup = cmd.group;
                        return (
                            <React.Fragment key={cmd.id}>
                                {header && <div className="cmdk__group">{header}</div>}
                                <button
                                    type="button"
                                    id={`cmdk-${index}`}
                                    data-index={index}
                                    role="option"
                                    aria-selected={index === active}
                                    className={`cmdk__item ${index === active ? 'is-active' : ''}`}
                                    onMouseMove={() => setActive(index)}
                                    onClick={cmd.run}>
                                    <span className="cmdk__icon">
                                        {cmd.productIcon
                                            ? <img src={cmd.productIcon} alt="" />
                                            : <i className={`bi ${cmd.icon}`} />}
                                    </span>
                                    <span className="cmdk__text">
                                        <strong>{cmd.label}</strong>
                                        {cmd.hint && <small>{cmd.hint}</small>}
                                    </span>
                                    {index === active && <i className="bi bi-arrow-return-left cmdk__enter" aria-hidden="true" />}
                                </button>
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="cmdk__footer">
                    <span><kbd>↑</kbd><kbd>↓</kbd> per muoverti</span>
                    <span><kbd>↵</kbd> per aprire</span>
                    {toolsState === 'error' && <span className="text-danger">Tool non caricati</span>}
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
